// slice-3562 (BigList #27) — the PB accelerator table, as DATA.
//
// PowerHouse's hot keys are not scattered through the screens: every one of them is a
// `shortcut=` + `~t<caption>` pair on a menu item in the live menu chain
// `m_frame -> m_main -> m_phfc_main` (`m_genapp_frame.srm` / `m_genapp_sheet.srm` are the
// untouched PB genapp templates and are NOT in the chain), plus five window-level handlers:
//
//   * `phfc_system_ancestors/w_phfc_base.srw:285-323` — the universal dispatcher. F1..F12
//     fan out to `ue_fkey01..12`, and `keyenter!` (:310-321) clicks the FOCUSED command
//     button.
//   * `phfc_system_ancestors/w_phfc_search.srw:137` (the caption "&Hide Criteria (Ctrl-Q)")
//     and `:147-149` (`keyflags = 2 and key = keyQ!`) — Ctrl+Q collapses the criteria pane.
//     85 routed windows inherit it.
//   * `phfc_system_ancestors/uo_phfc_dwdefault.sru:1260-1286` — three mouse+modifier
//     gestures on the DataWindow (Alt+click a cell = copy, Ctrl+click a header = sort,
//     Ctrl+Alt+click a header = multi-sort, then of_highlight for the row).
//   * `ph_activities_order_repack/w_carton_pickverify_ship.srw:4674` — Ctrl+E.
//   * `ph_activities_shipping_shippingstation/w_ship_station.srw:4108` — Space on the focused
//     grid row, and `:4110` Ctrl+D to expand/collapse a special-services group.
//
// This file is the single source of truth for all 39 of them. Bindings, toolbar tooltips and
// `docs/audits/biglist/27-hotkeys.md` are all generated from these rows so the key a user
// presses, the accelerator a tooltip advertises and the table Chris signs off can never
// drift apart. Callers bind ACTIONS, never keys — see `useShortcutAction`.
//
// Statuses mirror the audit's verdict column (docs/audits/biglist/03-toolbar-keys-tagging.md
// §5): `done` (already shipped), `bound` (honoured, possibly via a substitute key),
// `exception` (PB's key is owned by the browser/OS or by AG-Grid — a substitute is named and
// reported to Chris), `skip` (no PH10 equivalent exists), `deferred` (owned by another
// BigList item).

/** Stable action id. Pages and toolbars bind THESE, never raw keys. */
export type ShortcutActionId =
  // ---- app chrome (already live before this slice) ----
  | 'help.contents'
  | 'help.navigator'
  // ---- File menu ----
  | 'save'
  | 'print'
  | 'exit'
  | 'closeSheet'
  // ---- Edit menu ----
  | 'refresh'
  | 'new'
  | 'delete'
  | 'undo'
  | 'redo'
  | 'runReport'
  | 'switchView'
  | 'selectAll'
  | 'sort'
  | 'adHocSort'
  | 'filter'
  | 'adHocFilter'
  | 'find'
  | 'findNext'
  | 'findPrevious'
  | 'calendar'
  | 'reloadDropdown'
  | 'quickWindow'
  | 'customWindow'
  | 'itemLookup'
  // ---- window-level handlers ----
  | 'hideCriteria'
  | 'activateFocusedButton'
  | 'fkey10'
  | 'fkey11'
  | 'fkey12'
  // ---- Window menu ----
  | 'autoloadWindows'
  // ---- per-screen ----
  | 'addException'
  | 'shipStationValue'
  | 'expandGroup'
  // ---- DataWindow mouse gestures ----
  | 'copyCellToClipboard'
  | 'sortColumnSingle'
  | 'sortColumnMulti'
  | 'multiSelectRow';

export type ShortcutStatus = 'done' | 'bound' | 'exception' | 'skip' | 'deferred';

/** Which surface owns the implementation — drives the "wired vs remaining" report. */
export type ShortcutOwner = 'app' | 'toolbar' | 'grid' | 'screen' | 'browser';

export interface PbShortcut {
  readonly action: ShortcutActionId;
  /** How PowerHouse writes it in the menu caption after `~t` (or the gesture in prose). */
  readonly pbKey: string;
  /**
   * The combo PH10 listens for, in canonical `Ctrl+Alt+Shift+KEY` order, or `null` when the
   * key cannot be honoured at all (`skip` / unbindable `exception`). Gesture rows are `null`
   * too — the mouse gesture is described by {@link pbKey} and implemented by the grid.
   */
  readonly key: string | null;
  /** Extra combos accepted for the same action (e.g. Ctrl+Shift+Z as well as Ctrl+Y). */
  readonly alsoAccepts?: readonly string[];
  /** PB menu caption with `&` mnemonics and the `~t` accelerator stripped. */
  readonly label: string;
  /** `<file>:<line>` in PB-PH-Source-7-7-26 — the row that proves the key. */
  readonly pbSource: string;
  readonly status: ShortcutStatus;
  readonly owner: ShortcutOwner;
  /** Why PB's key cannot be used verbatim, and what replaces it. Required on `exception`. */
  readonly exception?: string;
  /**
   * Fire even while focus sits in a text input / open AG-Grid cell editor. True for every
   * Alt-combo and F-key (PB's `key` event belongs to the WINDOW, not the control); false
   * wherever the key means something to a text field (Ctrl+Z, Insert, Ctrl+Del, Space).
   */
  readonly allowInEditor: boolean;
  /** `preventDefault()` on match. Default true; false leaves the browser's default alone. */
  readonly preventDefault?: boolean;
  /**
   * The browser already does exactly what PB does, so the registry must NOT install a
   * listener (Enter on a focused button). Honoured, but by the platform.
   */
  readonly nativeOnly?: boolean;
  /**
   * Suppress while a modal dialog is open, so a page behind the modal cannot be saved or
   * refreshed out from under it. App-chrome keys (F1/F9) are deliberately NOT suppressed —
   * PB's Help accelerators fire from anywhere.
   */
  readonly blockedByDialog?: boolean;
  /** Extra guidance for the generated doc / for Chris. */
  readonly note?: string;
}

const SRM = 'phfc_system_ancestors/m_phfc_main.srm';
const SRM_MAIN = 'ph_system_ancestors/m_main.srm';
const BASE = 'phfc_system_ancestors/w_phfc_base.srw';
const SEARCH = 'phfc_system_ancestors/w_phfc_search.srw';
const DWDEF = 'phfc_system_ancestors/uo_phfc_dwdefault.sru';

/**
 * All 39 PB keys and gestures. Order follows the PB menus (File, Edit, Window, Help) then
 * the window-level handlers, then the mouse gestures — the same order as the audit table.
 */
export const PB_SHORTCUTS: readonly PbShortcut[] = [
  // ------------------------------------------------------------------ File
  {
    action: 'save',
    pbKey: 'Ctrl+S',
    key: 'Ctrl+S',
    label: 'Save',
    pbSource: `${SRM}:1300`,
    status: 'bound',
    owner: 'toolbar',
    // Chrome/Edge/Firefox all let a page preventDefault the "Save Page As" dialog, and a
    // half-typed field is exactly when an operator hits Ctrl+S — so it fires in editors too.
    allowInEditor: true,
    blockedByDialog: true,
  },
  {
    action: 'print',
    pbKey: 'Ctrl+P',
    key: 'Ctrl+P',
    label: 'Print',
    pbSource: `${SRM}:1640`,
    status: 'bound',
    owner: 'toolbar',
    allowInEditor: true,
    blockedByDialog: true,
    note: 'Routes to the page Print action (which renders the PB report) instead of the browser printing the raw HTML.',
  },
  {
    action: 'exit',
    pbKey: 'Alt+F4',
    key: null,
    label: 'Exit',
    pbSource: `${SRM}:2128`,
    status: 'exception',
    owner: 'browser',
    exception:
      'The OS closes the window; a page cannot intercept Alt+F4. No PH10 equivalent — skipped.',
    allowInEditor: false,
  },
  {
    action: 'closeSheet',
    pbKey: 'Alt+Z',
    key: null,
    label: 'Close',
    pbSource: `${SRM}:1203`,
    status: 'skip',
    owner: 'browser',
    allowInEditor: false,
    note: 'PH10 has no MDI sheets; the browser tab / Back is the equivalent (BigList #15).',
  },
  // ------------------------------------------------------------------ Edit
  {
    action: 'refresh',
    pbKey: 'F5',
    key: 'Alt+R',
    label: 'Refresh',
    pbSource: `${SRM}:3920`,
    status: 'exception',
    owner: 'toolbar',
    exception:
      'F5 is browser reload. Even with preventDefault the reload button and muscle memory both still mean "reload", so F5 stays the browser\'s. Refresh gets Alt+R and the button is labelled "Refresh (Alt+R)".',
    allowInEditor: true,
    blockedByDialog: true,
  },
  {
    action: 'new',
    pbKey: 'Ins',
    key: 'Insert',
    label: 'New',
    pbSource: `${SRM}:4004`,
    status: 'bound',
    owner: 'toolbar',
    // Insert toggles overwrite mode in a text field — leave it alone there.
    allowInEditor: false,
    blockedByDialog: true,
  },
  {
    action: 'delete',
    pbKey: 'Ctrl+Del',
    key: 'Ctrl+Delete',
    label: 'Delete',
    pbSource: `${SRM}:4059`,
    status: 'bound',
    owner: 'toolbar',
    // Ctrl+Delete deletes a word in a text field.
    allowInEditor: false,
    blockedByDialog: true,
  },
  {
    action: 'undo',
    pbKey: 'Ctrl+Z',
    key: 'Ctrl+Z',
    label: 'Undo',
    pbSource: `${SRM}:4094`,
    status: 'bound',
    owner: 'grid',
    allowInEditor: false,
    blockedByDialog: true,
    note: 'Row-level undo at grid level; inside an open cell editor the browser keeps native undo.',
  },
  {
    action: 'redo',
    pbKey: 'Ctrl+Y',
    key: 'Ctrl+Y',
    alsoAccepts: ['Ctrl+Shift+Z'],
    label: 'Redo',
    pbSource: `${SRM}:4140`,
    status: 'bound',
    owner: 'grid',
    allowInEditor: false,
    blockedByDialog: true,
  },
  {
    action: 'runReport',
    pbKey: 'Ctrl+R',
    key: 'Alt+Shift+R',
    label: 'Run Report',
    pbSource: `${SRM}:4197`,
    status: 'exception',
    owner: 'toolbar',
    exception:
      'Ctrl+R is browser reload and is not reliably preventable across browser/OS combinations. Alt+R went to Refresh (365 screens) so Run Report takes Alt+Shift+R.',
    allowInEditor: true,
    blockedByDialog: true,
  },
  {
    action: 'switchView',
    pbKey: 'Ctrl+W',
    key: 'Alt+V',
    label: 'Switch View',
    pbSource: `${SRM}:4291`,
    status: 'exception',
    owner: 'toolbar',
    exception:
      'Ctrl+W closes the browser TAB and Chrome/Edge will not let a page prevent it. Switch View takes Alt+V.',
    allowInEditor: true,
    blockedByDialog: true,
  },
  {
    action: 'selectAll',
    pbKey: 'Ctrl+A',
    key: 'Ctrl+A',
    label: 'Select All',
    pbSource: `${SRM}:4358`,
    status: 'bound',
    owner: 'grid',
    allowInEditor: false,
    blockedByDialog: true,
    note: 'Grid focus only — inside inputs the browser keeps select-all-text. Depends on BigList #21 widening rowSelection.',
  },
  {
    action: 'sort',
    pbKey: 'F2',
    key: 'Alt+F2',
    label: 'Sort...',
    pbSource: `${SRM}:4608`,
    status: 'exception',
    owner: 'toolbar',
    exception:
      "AG-Grid owns F2 = start editing the focused cell (the Excel convention BigList #2/#2a lean on). Sort keeps its toolbar button and takes Alt+F2 (Chris, 2026-09-04). PB's own Alt+F2 (Ad Hoc Sort) therefore moves to Alt+Shift+F2 — flagged for Chris in 27-hotkeys.md.",
    allowInEditor: true,
    blockedByDialog: true,
  },
  {
    action: 'adHocSort',
    pbKey: 'Alt+F2',
    key: 'Alt+Shift+F2',
    label: 'Ad Hoc Sort',
    pbSource: `${SRM}:4668`,
    status: 'exception',
    owner: 'toolbar',
    exception:
      'Displaced by the F2 ruling above: Sort... took Alt+F2, so Ad Hoc Sort shifts one modifier to Alt+Shift+F2. The only PB key PH10 moves for a reason other than the browser.',
    allowInEditor: true,
    blockedByDialog: true,
  },
  {
    action: 'filter',
    pbKey: 'F3',
    key: 'F3',
    label: 'Filter',
    pbSource: `${SRM}:4742`,
    status: 'bound',
    owner: 'toolbar',
    allowInEditor: true,
    blockedByDialog: true,
    note: "preventDefault suppresses Firefox's Find Again.",
  },
  {
    action: 'adHocFilter',
    pbKey: 'Alt+F3',
    key: 'Alt+F3',
    label: 'Ad Hoc Filter',
    pbSource: `${SRM}:4811`,
    status: 'bound',
    owner: 'toolbar',
    allowInEditor: true,
    blockedByDialog: true,
  },
  {
    action: 'find',
    pbKey: 'F4',
    key: 'F4',
    label: 'Find...',
    pbSource: `${SRM}:4917`,
    status: 'bound',
    owner: 'toolbar',
    allowInEditor: true,
    blockedByDialog: true,
  },
  {
    action: 'findNext',
    pbKey: 'F6',
    key: 'F6',
    label: 'Find Next',
    pbSource: `${SRM}:4977`,
    status: 'bound',
    owner: 'toolbar',
    allowInEditor: true,
    blockedByDialog: true,
    note: "preventDefault suppresses the browser's cycle-focus-between-panes.",
  },
  {
    action: 'findPrevious',
    pbKey: 'F7',
    key: 'F7',
    label: 'Find Previous',
    pbSource: `${SRM}:5041`,
    status: 'bound',
    owner: 'toolbar',
    allowInEditor: true,
    blockedByDialog: true,
    note: "preventDefault suppresses Firefox's caret-browsing prompt.",
  },
  {
    action: 'calendar',
    pbKey: 'Alt+D',
    key: 'Alt+D',
    label: 'Calendar',
    pbSource: `${SRM}:5221`,
    status: 'bound',
    owner: 'screen',
    // The whole point is that focus is IN the date field.
    allowInEditor: true,
    blockedByDialog: true,
  },
  {
    action: 'reloadDropdown',
    pbKey: 'Alt+X',
    key: 'Alt+X',
    label: 'Reload Dropdown',
    pbSource: `${SRM}:5266`,
    status: 'bound',
    owner: 'screen',
    allowInEditor: true,
    blockedByDialog: true,
    note: "Invalidates the focused select's lookup query.",
  },
  {
    action: 'quickWindow',
    pbKey: 'Alt+Q',
    key: 'Alt+Q',
    label: 'Quick Window',
    pbSource: `${SRM}:5306`,
    status: 'bound',
    owner: 'app',
    allowInEditor: true,
    note: 'The rabbit — BigList #24 builds the picker; this slice reserves the key and the action slot.',
  },
  {
    action: 'customWindow',
    pbKey: 'Alt+U',
    key: 'Alt+U',
    label: 'Custom Window',
    pbSource: `${SRM}:5353`,
    status: 'bound',
    owner: 'app',
    allowInEditor: true,
    note: 'Mode-filtered Quick Window — BigList #24.',
  },
  {
    action: 'itemLookup',
    pbKey: 'Alt+L',
    key: 'Alt+L',
    label: 'Item Lookup',
    pbSource: `${SRM_MAIN}:7998`,
    status: 'bound',
    owner: 'grid',
    allowInEditor: true,
    note: 'Item lookup into the focused cell — BigList #24.',
  },
  // ---------------------------------------------------- window-level handlers
  {
    action: 'hideCriteria',
    pbKey: 'Ctrl+Q',
    key: 'Ctrl+Q',
    label: 'Hide Criteria',
    pbSource: `${SEARCH}:137,147`,
    status: 'bound',
    owner: 'toolbar',
    // Operators hit it mid-typing in a criteria field; PB's key event is the WINDOW's.
    allowInEditor: true,
    blockedByDialog: true,
    note: '85 routed windows inherit it from w_phfc_search. Free in Chrome/Edge/Firefox on Windows.',
  },
  {
    action: 'activateFocusedButton',
    pbKey: 'Enter',
    key: 'Enter',
    label: 'Activate the focused button',
    pbSource: `${BASE}:310`,
    status: 'bound',
    owner: 'browser',
    allowInEditor: false,
    nativeOnly: true,
    note: 'The browser already clicks a focused <button> on Enter, which is exactly what w_phfc_base::key does. The registry installs NO listener here — swallowing Enter would break the scanner field-exit contract (dev-rules tab-order rule).',
  },
  {
    action: 'fkey10',
    pbKey: 'F10',
    key: 'F10',
    label: 'Screen action (F10)',
    pbSource: `${SRM}:12897`,
    status: 'bound',
    owner: 'screen',
    allowInEditor: true,
    blockedByDialog: true,
    note: 'Per screen: w_carton_pickverify_ship = Pick Verify, w_ship_station = tab/field hop. preventDefault suppresses focus-the-menu-bar.',
  },
  {
    action: 'fkey11',
    pbKey: 'F11',
    key: 'Alt+F11',
    label: 'Screen action (F11)',
    pbSource: `${SRM}:12938`,
    status: 'exception',
    owner: 'screen',
    exception:
      'F11 is the full-screen toggle and preventDefault is unreliable. Alt+F11, plus a visible button on the 2 screens that use it.',
    allowInEditor: true,
    blockedByDialog: true,
  },
  {
    action: 'fkey12',
    pbKey: 'F12',
    key: 'Alt+F12',
    label: 'Screen action (F12)',
    pbSource: `${SRM}:12980`,
    status: 'exception',
    owner: 'screen',
    exception:
      'F12 opens devtools and is not preventable in Chrome. Alt+F12, plus a visible button on the 6 screens that use it.',
    allowInEditor: true,
    blockedByDialog: true,
  },
  // ---------------------------------------------------------------- Window
  {
    action: 'autoloadWindows',
    pbKey: 'F8',
    key: null,
    label: 'Load AutoLoad Windows',
    pbSource: `${SRM}:11067`,
    status: 'skip',
    owner: 'app',
    allowInEditor: false,
    note: 'Needs the autoload-window feature first; nothing to bind to.',
  },
  // ------------------------------------------------------------------ Help
  {
    action: 'help.contents',
    pbKey: 'F1',
    key: 'F1',
    label: 'Help Contents',
    pbSource: `${SRM}:11797`,
    status: 'done',
    owner: 'app',
    // PB resolves the ACTIVE sheet's window name wherever focus is — so does PH10 (slice-3454).
    allowInEditor: true,
  },
  {
    action: 'help.navigator',
    pbKey: 'F9',
    key: 'F9',
    label: 'Menu Navigator',
    pbSource: `${SRM}:11910`,
    status: 'done',
    owner: 'app',
    allowInEditor: true,
  },
  // ------------------------------------------------------------- per-screen
  {
    action: 'addException',
    pbKey: 'Ctrl+E',
    key: 'Ctrl+E',
    label: 'Add Exception',
    pbSource: 'ph_activities_order_repack/w_carton_pickverify_ship.srw:4674',
    status: 'bound',
    owner: 'screen',
    // Ctrl+E is end-of-line in a text field on macOS — leave editors alone.
    allowInEditor: false,
    blockedByDialog: true,
    note: '/browse/carton-pickverify-ship only; the AddExceptionModal already exists.',
  },
  {
    action: 'shipStationValue',
    pbKey: 'Space',
    key: 'Space',
    label: 'Get ship-station value',
    pbSource: 'ph_activities_shipping_shippingstation/w_ship_station.srw:4108',
    status: 'bound',
    owner: 'screen',
    allowInEditor: false,
    blockedByDialog: true,
    note: '/ship/station grid rows only (wf_get_ss_value); preventDefault stops the page scrolling.',
  },
  {
    action: 'expandGroup',
    pbKey: 'Ctrl+D',
    key: 'Ctrl+D',
    label: 'Expand/collapse group',
    pbSource: 'ph_activities_shipping_shippingstation/w_ship_station.srw:4110',
    status: 'bound',
    owner: 'screen',
    // Ctrl+D is Chrome/Edge/Firefox "bookmark this page" — but unlike Ctrl+W it IS
    // preventable, so PB's key survives verbatim.
    allowInEditor: false,
    blockedByDialog: true,
    note: 'Second branch of the same dw_1::keydown as Space (:4108). wf_expand (:1436) toggles the special-services group filter for the focused group_master row; the same toggle is also a click on the "plus" column (:4062), so the key is an accelerator for an affordance that is already visible. /ship/station only. NOT in the audit table — found while verifying its citations.',
  },
  // ------------------------------------------------- DataWindow mouse gestures
  {
    action: 'copyCellToClipboard',
    pbKey: 'Alt+click a grid cell',
    key: null,
    label: 'Copy cell value',
    pbSource: `${DWDEF}:1260`,
    status: 'bound',
    owner: 'grid',
    allowInEditor: false,
    note: 'f_phfc_copy_column_data — a mouse gesture, implemented in PhGrid (BigList #27 repair lane R-C), not by the key dispatcher.',
  },
  {
    action: 'sortColumnSingle',
    pbKey: 'Ctrl+click a column header',
    key: null,
    label: 'Sort this column',
    pbSource: `${DWDEF}:1267`,
    status: 'exception',
    owner: 'grid',
    exception:
      'AG-Grid sorts on a PLAIN header click; requiring Ctrl would be worse web behaviour. PH10 accepts the plain click AND also honours Ctrl+click.',
    allowInEditor: false,
  },
  {
    action: 'sortColumnMulti',
    pbKey: 'Ctrl+Alt+click a column header',
    key: null,
    label: 'Add column to the sort',
    pbSource: `${DWDEF}:1272`,
    status: 'exception',
    owner: 'grid',
    exception:
      "AG-Grid's multi-sort modifier is Shift. PH10 sets multiSortKey and additionally accepts Ctrl+Alt+click.",
    allowInEditor: false,
  },
  {
    action: 'multiSelectRow',
    pbKey: 'Ctrl+click / Shift+click a row',
    key: null,
    label: 'Multi-row select',
    pbSource: `${DWDEF}:1284`,
    status: 'deferred',
    owner: 'grid',
    allowInEditor: false,
    note: 'of_highlight — owned by BigList #21 (rowSelection widening), not by this slice.',
  },
];

const BY_ACTION = new Map<ShortcutActionId, PbShortcut>(
  PB_SHORTCUTS.map((s) => [s.action, s]),
);

export function pbShortcut(action: ShortcutActionId): PbShortcut {
  const found = BY_ACTION.get(action);
  // Unreachable through the typed API; a throw beats a silent no-op binding.
  if (!found) throw new Error(`Unknown shortcut action: ${action}`);
  return found;
}

/**
 * The accelerator to advertise for an action ("Alt+R"), or null when there is nothing to
 * advertise (skipped keys, mouse gestures, and Enter which the browser already owns).
 */
export function shortcutAccelerator(action: ShortcutActionId): string | null {
  const s = BY_ACTION.get(action);
  if (!s || s.key === null || s.nativeOnly) return null;
  return s.key;
}

/**
 * PB writes the accelerator into the caption itself ("&Refresh~tF5", "&Hide Criteria (Ctrl-Q)").
 * PH10 does the same for tooltips — but never twice: a label that already names the key is
 * left exactly as the PB caption has it, which is what the pinned `title="<PB caption>"`
 * assertions across the fleet depend on.
 */
export function withAccelerator(label: string, action: ShortcutActionId): string {
  const accel = shortcutAccelerator(action);
  if (!accel) return label;
  // "Ctrl-Q" and "Ctrl+Q" are the same key to a reader; compare with separators removed.
  const flat = (t: string) => t.toLowerCase().replace(/[-+\s()]/g, '');
  if (flat(label).includes(flat(accel))) return label;
  return `${label} (${accel})`;
}
